// ═══════════════════════════════════════════════════════════════
// KOTCH - GAME LOGIC
// Main reducer and state management
// ═══════════════════════════════════════════════════════════════

import type {
  GameState,
  GameAction,
  GameConfig,
  Player,
  PlayerId,
  BattleState,
  MatchState,
  LaneCommitment,
  Lane,
  CardInstance,
} from '../../types/kotch';
import { createEmptyCommitment, PLAYER_COLORS } from '../../types/kotch';
import { createFullDeck, createFactionDeck, shuffleDeck, drawCards, removeCardFromArray, resetInstanceCounter } from './deckUtils';
import { resolveRound, isBattleOver, determineBattleWinner, isMatchOver, determineMatchWinner, countCommittedCards, applyLaneRewards, checkForRouting } from './combatUtils';
import { CARDS_PER_BATTLE } from '../../data/kotch/gameConstants';

// ─────────────────────────────────────────────────────────────────
// INITIAL STATE CREATION
// ─────────────────────────────────────────────────────────────────

export function createInitialState(): GameState {
  return {
    phase: 'setup',
    commitmentSubPhase: null,
    config: {
      player1Setup: { name: 'Player 1', isAI: false },
      player2Setup: { name: 'Opponent', isAI: true, aiPersonality: 'medium' },
      cardsPerBattle: CARDS_PER_BATTLE,
      battlesToWin: 2,
    },
    players: {
      1: createEmptyPlayer(1),
      2: createEmptyPlayer(2),
    },
    match: createInitialMatchState(),
    currentAttackerId: 1,
    currentDefenderId: 2,
    player1Commitment: createEmptyCommitment(),
    player2Commitment: createEmptyCommitment(),
    revealed: false,
    currentRoundResult: null,
    resolvingLaneIndex: 0,
    persistentCard: null,
    lockedLane: null,
    pendingAbility: null,
    selectedCardInstanceId: null,
    hoveredLane: null,
    message: null,
    animating: false,
    transitioningPlayer: null,
    winner: null,
  };
}

function createEmptyPlayer(id: PlayerId): Player {
  return {
    id,
    name: id === 1 ? 'Player 1' : 'Player 2',
    color: id === 1 ? PLAYER_COLORS.player1 : PLAYER_COLORS.player2,
    isAI: false,
    aiPersonality: null,
    faction: null,
    hand: [],
    matchDeck: [],
    discardPile: [],
    revealedCards: [],
  };
}

function createInitialMatchState(): MatchState {
  return {
    player1BattleWins: 0,
    player2BattleWins: 0,
    battlesPlayed: 0,
    battlesToWin: 2,
    currentBattle: createInitialBattleState(1),
    battleHistory: [],
  };
}

function createInitialBattleState(battleNumber: number): BattleState {
  return {
    battleNumber,
    roundNumber: 1,
    roundsPlayed: [],
    player1RoundWins: 0,
    player2RoundWins: 0,
    roundDraws: 0,
    firstAttackerId: 1,
    isComplete: false,
    winnerId: null,
    winReason: '',
  };
}

// ─────────────────────────────────────────────────────────────────
// MAIN REDUCER
// ─────────────────────────────────────────────────────────────────

export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'START_GAME':
      return handleStartGame(state, action.config);

    case 'RESET_GAME':
      return createInitialState();

    case 'START_BATTLE':
      return handleStartBattle(state);

    case 'START_ROUND':
      return handleStartRound(state);

    case 'CONFIRM_PLAYER_READY':
      return handleConfirmPlayerReady(state);

    case 'SELECT_CARD':
      return { ...state, selectedCardInstanceId: action.cardInstanceId };

    case 'HOVER_LANE':
      return { ...state, hoveredLane: action.lane };

    case 'COMMIT_CARD_TO_LANE':
      return handleCommitCardToLane(state, action.lane);

    case 'UNCOMMIT_CARD_FROM_LANE':
      return handleUncommitCardFromLane(state, action.lane);

    case 'CONFIRM_COMMITMENT':
      return handleConfirmCommitment(state);

    case 'REVEAL_CARDS':
      return handleRevealCards(state);

    case 'RESOLVE_NEXT_LANE':
      return handleResolveNextLane(state);

    case 'COMPLETE_ROUND_RESOLUTION':
      return handleCompleteRoundResolution(state);

    case 'SELECT_DISCARD_TARGET':
      return handleSelectDiscardTarget(state, action.cardInstanceId);

    case 'CONFIRM_ROUND_END':
      return handleConfirmRoundEnd(state);

    case 'CONFIRM_BATTLE_END':
      return handleConfirmBattleEnd(state);

    case 'AI_MAKE_COMMITMENT':
      return handleAIMakeCommitment(state, action.commitment);

    case 'SET_MESSAGE':
      return { ...state, message: action.message };

    case 'SET_ANIMATING':
      return { ...state, animating: action.animating };

    case 'SET_STATE':
      return action.state;

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────
// ACTION HANDLERS
// ─────────────────────────────────────────────────────────────────

function handleStartGame(state: GameState, config: GameConfig): GameState {
  resetInstanceCounter();

  // Create faction-based decks if faction is specified, otherwise use full deck
  const deck1 = config.player1Setup.faction
    ? createFactionDeck(config.player1Setup.faction)
    : shuffleDeck(createFullDeck());

  const deck2 = config.player2Setup.faction
    ? createFactionDeck(config.player2Setup.faction)
    : shuffleDeck(createFullDeck());

  // Create players
  const player1: Player = {
    id: 1,
    name: config.player1Setup.name,
    color: config.player1Setup.color || PLAYER_COLORS.player1,
    isAI: config.player1Setup.isAI,
    aiPersonality: config.player1Setup.aiPersonality || null,
    faction: config.player1Setup.faction || null,
    hand: [],
    matchDeck: deck1,
    discardPile: [],
    revealedCards: [],
  };

  const player2: Player = {
    id: 2,
    name: config.player2Setup.name,
    color: config.player2Setup.color || PLAYER_COLORS.player2,
    isAI: config.player2Setup.isAI,
    aiPersonality: config.player2Setup.aiPersonality || null,
    faction: config.player2Setup.faction || null,
    hand: [],
    matchDeck: deck2,
    discardPile: [],
    revealedCards: [],
  };

  // Randomly determine first attacker
  const firstAttacker: PlayerId = Math.random() < 0.5 ? 1 : 2;

  return {
    ...state,
    phase: 'battleStart',
    config,
    players: { 1: player1, 2: player2 },
    match: {
      ...createInitialMatchState(),
      battlesToWin: config.battlesToWin,
      currentBattle: {
        ...createInitialBattleState(1),
        firstAttackerId: firstAttacker,
      },
    },
    currentAttackerId: firstAttacker,
    currentDefenderId: firstAttacker === 1 ? 2 : 1,
  };
}

function handleStartBattle(state: GameState): GameState {
  const cardsPerBattle = state.config.cardsPerBattle;

  // Draw hands for both players from their match decks
  const p1Draw = drawCards(state.players[1].matchDeck, cardsPerBattle);
  const p2Draw = drawCards(state.players[2].matchDeck, cardsPerBattle);

  const updatedPlayers = {
    1: {
      ...state.players[1],
      hand: p1Draw.drawn,
      matchDeck: p1Draw.remaining,
      revealedCards: [],  // Clear revealed cards at battle start
    },
    2: {
      ...state.players[2],
      hand: p2Draw.drawn,
      matchDeck: p2Draw.remaining,
      revealedCards: [],  // Clear revealed cards at battle start
    },
  };

  return {
    ...state,
    phase: 'playerTransition',
    players: updatedPlayers,
    transitioningPlayer: state.currentAttackerId,
    player1Commitment: createEmptyCommitment(),
    player2Commitment: createEmptyCommitment(),
    revealed: false,
    currentRoundResult: null,
    selectedCardInstanceId: null,
    message: null,
  };
}

function handleStartRound(state: GameState): GameState {
  let p1Commitment = createEmptyCommitment();
  let p2Commitment = createEmptyCommitment();
  let lockedLane: GameState['lockedLane'] = null;

  // Motra persistent card: pre-commit to a flank lane
  if (state.persistentCard) {
    const { card, playerId } = state.persistentCard;
    const commitment = playerId === 1 ? p1Commitment : p2Commitment;
    const lane: Lane = 'left';
    commitment[lane] = card;
    if (playerId === 1) p1Commitment = commitment;
    else p2Commitment = commitment;
    lockedLane = { playerId, lane };
  }

  return {
    ...state,
    phase: 'playerTransition',
    transitioningPlayer: state.currentAttackerId,
    player1Commitment: p1Commitment,
    player2Commitment: p2Commitment,
    persistentCard: null,
    lockedLane,
    revealed: false,
    currentRoundResult: null,
    selectedCardInstanceId: null,
    resolvingLaneIndex: 0,
  };
}

function handleConfirmPlayerReady(state: GameState): GameState {
  const currentPlayer = state.transitioningPlayer;

  if (!currentPlayer) {
    return { ...state, phase: 'commitment', commitmentSubPhase: 'attackerCommit' };
  }

  const isAttacker = currentPlayer === state.currentAttackerId;

  if (isAttacker) {
    // Attacker is ready, enter commitment phase
    return {
      ...state,
      phase: 'commitment',
      commitmentSubPhase: 'attackerCommit',
      transitioningPlayer: null,
    };
  } else {
    // Defender is ready, enter defender commitment phase
    return {
      ...state,
      phase: 'commitment',
      commitmentSubPhase: 'defenderCommit',
      transitioningPlayer: null,
    };
  }
}

function handleCommitCardToLane(state: GameState, lane: Lane): GameState {
  const selectedCardId = state.selectedCardInstanceId;
  if (!selectedCardId) return state;

  const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
  const playerId: PlayerId = isAttackerTurn ? state.currentAttackerId : state.currentDefenderId;
  const player = state.players[playerId];

  // Find the card in hand
  const card = player.hand.find(c => c.instanceId === selectedCardId);
  if (!card) return state;

  // Get current commitment
  const commitmentKey = playerId === 1 ? 'player1Commitment' : 'player2Commitment';
  const currentCommitment = state[commitmentKey];

  // Check if lane is already occupied
  if (currentCommitment[lane] !== null) return state;

  // Remove card from hand and add to commitment
  const newHand = removeCardFromArray(player.hand, selectedCardId);
  const newCommitment: LaneCommitment = {
    ...currentCommitment,
    [lane]: card,
  };

  return {
    ...state,
    players: {
      ...state.players,
      [playerId]: { ...player, hand: newHand },
    },
    [commitmentKey]: newCommitment,
    selectedCardInstanceId: null,
  };
}

function handleUncommitCardFromLane(state: GameState, lane: Lane): GameState {
  const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
  const playerId: PlayerId = isAttackerTurn ? state.currentAttackerId : state.currentDefenderId;
  const player = state.players[playerId];

  const commitmentKey = playerId === 1 ? 'player1Commitment' : 'player2Commitment';
  const currentCommitment = state[commitmentKey];

  const card = currentCommitment[lane];
  if (!card) return state;

  // Prevent uncommitting a persistent (locked) card
  if (state.lockedLane && state.lockedLane.playerId === playerId && state.lockedLane.lane === lane) {
    return state;
  }

  // Return card to hand
  const newHand = [...player.hand, card];
  const newCommitment: LaneCommitment = {
    ...currentCommitment,
    [lane]: null,
  };

  return {
    ...state,
    players: {
      ...state.players,
      [playerId]: { ...player, hand: newHand },
    },
    [commitmentKey]: newCommitment,
  };
}

function handleConfirmCommitment(state: GameState): GameState {
  if (state.commitmentSubPhase === 'attackerCommit') {
    // Attacker done, now defender's turn
    const defender = state.players[state.currentDefenderId];

    if (defender.isAI) {
      // AI defender will commit via AI_MAKE_COMMITMENT action
      return {
        ...state,
        commitmentSubPhase: 'defenderCommit',
      };
    } else {
      // Human defender needs transition screen
      return {
        ...state,
        phase: 'playerTransition',
        transitioningPlayer: state.currentDefenderId,
        commitmentSubPhase: 'defenderCommit',
      };
    }
  } else {
    // Defender done, reveal cards
    return {
      ...state,
      phase: 'reveal',
      commitmentSubPhase: null,
    };
  }
}

function handleRevealCards(state: GameState): GameState {
  // Resolve the round
  const roundResult = resolveRound(
    state.player1Commitment,
    state.player2Commitment,
    state.match.currentBattle.roundNumber,
    state.currentAttackerId
  );

  return {
    ...state,
    revealed: true,
    currentRoundResult: roundResult,
    phase: 'resolution',
    resolvingLaneIndex: 0,
  };
}

function handleResolveNextLane(state: GameState): GameState {
  const nextIndex = state.resolvingLaneIndex + 1;

  if (nextIndex >= 3) {
    // All lanes resolved
    return state;
  }

  return {
    ...state,
    resolvingLaneIndex: nextIndex,
  };
}

function handleCompleteRoundResolution(state: GameState): GameState {
  if (!state.currentRoundResult) return state;

  const roundResult = state.currentRoundResult;
  const battle = state.match.currentBattle;

  // Update round wins
  let p1Wins = battle.player1RoundWins;
  let p2Wins = battle.player2RoundWins;
  let draws = battle.roundDraws;

  if (roundResult.winnerId === 1) {
    p1Wins++;
  } else if (roundResult.winnerId === 2) {
    p2Wins++;
  } else {
    draws++;
  }

  // Move committed cards to discard
  const p1Discards = [
    state.player1Commitment.left,
    state.player1Commitment.center,
    state.player1Commitment.right,
  ].filter((c): c is CardInstance => c !== null);

  const p2Discards = [
    state.player2Commitment.left,
    state.player2Commitment.center,
    state.player2Commitment.right,
  ].filter((c): c is CardInstance => c !== null);

  // First update players with discarded cards
  let updatedPlayers: Record<PlayerId, Player> = {
    1: {
      ...state.players[1],
      discardPile: [...state.players[1].discardPile, ...p1Discards],
    },
    2: {
      ...state.players[2],
      discardPile: [...state.players[2].discardPile, ...p2Discards],
    },
  };

  // Apply lane rewards (pass commitments for Migora/Motra abilities)
  const { updatedPlayers: playersAfterRewards, rewards, persistentCard, pendingAbility } = applyLaneRewards(
    roundResult.laneResults,
    updatedPlayers,
    state.player1Commitment,
    state.player2Commitment
  );

  // Update round result with lane rewards
  const updatedRoundResult = {
    ...roundResult,
    laneRewards: rewards,
  };

  const updatedBattle: BattleState = {
    ...battle,
    roundNumber: battle.roundNumber + 1,
    roundsPlayed: [...battle.roundsPlayed, updatedRoundResult],
    player1RoundWins: p1Wins,
    player2RoundWins: p2Wins,
    roundDraws: draws,
  };

  // If Hydaria won center, pause for player to pick discard target
  const nextPhase = pendingAbility ? 'factionAbilitySelect' : 'roundEnd';

  return {
    ...state,
    phase: nextPhase,
    players: playersAfterRewards,
    currentRoundResult: updatedRoundResult,
    persistentCard,
    pendingAbility,
    match: {
      ...state.match,
      currentBattle: updatedBattle,
    },
  };
}

function handleSelectDiscardTarget(state: GameState, cardInstanceId: string): GameState {
  if (state.phase !== 'factionAbilitySelect' || !state.pendingAbility) return state;

  const { loserId } = state.pendingAbility;

  // Verify the selected card is eligible
  if (!state.pendingAbility.eligibleCardIds.includes(cardInstanceId)) return state;

  // Find and remove the card from the loser's hand
  const loserHand = [...state.players[loserId].hand];
  const handIdx = loserHand.findIndex(c => c.instanceId === cardInstanceId);
  if (handIdx === -1) return state;

  const discardedCard = loserHand[handIdx];
  loserHand.splice(handIdx, 1);

  // Update revealed cards if applicable
  const revealedCards = [...state.players[loserId].revealedCards];
  const revIdx = revealedCards.indexOf(cardInstanceId);
  if (revIdx !== -1) revealedCards.splice(revIdx, 1);

  return {
    ...state,
    phase: 'roundEnd',
    pendingAbility: null,
    players: {
      ...state.players,
      [loserId]: {
        ...state.players[loserId],
        hand: loserHand,
        discardPile: [...state.players[loserId].discardPile, discardedCard],
        revealedCards,
      },
    },
  };
}

function handleConfirmRoundEnd(state: GameState): GameState {
  const p1 = state.players[1];
  const p2 = state.players[2];

  // Check for routing: one player has 0 cards while the other still has cards
  // The player with remaining cards gains bonus round wins equal to their remaining cards
  const routingResult = checkForRouting(p1.hand, p2.hand);

  if (routingResult.isRouted) {
    // Add bonus round wins to the player with remaining cards
    const p1RoundWins = state.match.currentBattle.player1RoundWins +
      (routingResult.winnerPlayerId === 1 ? routingResult.bonusPoints : 0);
    const p2RoundWins = state.match.currentBattle.player2RoundWins +
      (routingResult.winnerPlayerId === 2 ? routingResult.bonusPoints : 0);

    // Determine winner based on final round wins
    let winnerId: PlayerId | null = null;
    if (p1RoundWins > p2RoundWins) {
      winnerId = 1;
    } else if (p2RoundWins > p1RoundWins) {
      winnerId = 2;
    }
    // If still tied, use the normal tiebreaker logic
    if (winnerId === null) {
      const tiebreaker = determineBattleWinner(p1RoundWins, p2RoundWins, state.match.currentBattle.roundsPlayed);
      winnerId = tiebreaker.winnerId;
    }

    const routedPlayerName = state.players[routingResult.routedPlayerId!].name;
    const bonusPlayerName = state.players[routingResult.winnerPlayerId!].name;
    const winnerRounds = winnerId === 1 ? p1RoundWins : p2RoundWins;
    const loserRounds = winnerId === 1 ? p2RoundWins : p1RoundWins;

    const updatedBattle: BattleState = {
      ...state.match.currentBattle,
      player1RoundWins: p1RoundWins,
      player2RoundWins: p2RoundWins,
      isComplete: true,
      winnerId,
      winReason: `${routedPlayerName} was routed! ${bonusPlayerName} gains +${routingResult.bonusPoints}. Final: ${winnerRounds} to ${loserRounds}`,
    };

    // Update match battle wins
    let p1BattleWins = state.match.player1BattleWins;
    let p2BattleWins = state.match.player2BattleWins;

    if (winnerId === 1) p1BattleWins++;
    else if (winnerId === 2) p2BattleWins++;

    return {
      ...state,
      phase: 'battleEnd',
      match: {
        ...state.match,
        currentBattle: updatedBattle,
        player1BattleWins: p1BattleWins,
        player2BattleWins: p2BattleWins,
        battlesPlayed: state.match.battlesPlayed + 1,
        battleHistory: [...state.match.battleHistory, updatedBattle],
      },
    };
  }

  // Check if battle is over (both players out of cards)
  if (isBattleOver(p1.hand, p2.hand)) {
    const battleResult = determineBattleWinner(
      state.match.currentBattle.player1RoundWins,
      state.match.currentBattle.player2RoundWins,
      state.match.currentBattle.roundsPlayed
    );

    const updatedBattle: BattleState = {
      ...state.match.currentBattle,
      isComplete: true,
      winnerId: battleResult.winnerId,
      winReason: battleResult.winReason,
    };

    // Update match battle wins
    let p1BattleWins = state.match.player1BattleWins;
    let p2BattleWins = state.match.player2BattleWins;

    if (battleResult.winnerId === 1) p1BattleWins++;
    else if (battleResult.winnerId === 2) p2BattleWins++;

    return {
      ...state,
      phase: 'battleEnd',
      match: {
        ...state.match,
        currentBattle: updatedBattle,
        player1BattleWins: p1BattleWins,
        player2BattleWins: p2BattleWins,
        battlesPlayed: state.match.battlesPlayed + 1,
        battleHistory: [...state.match.battleHistory, updatedBattle],
      },
    };
  }

  // Battle continues - swap attacker/defender, then start next round
  // (handleStartRound handles persistent card pre-commit for Motra)
  return handleStartRound({
    ...state,
    currentAttackerId: state.currentDefenderId,
    currentDefenderId: state.currentAttackerId,
  });
}

function handleConfirmBattleEnd(state: GameState): GameState {
  const { player1BattleWins, player2BattleWins, battlesToWin } = state.match;

  // Check if match is over
  if (isMatchOver(player1BattleWins, player2BattleWins, battlesToWin)) {
    const matchWinner = determineMatchWinner(player1BattleWins, player2BattleWins, battlesToWin);

    return {
      ...state,
      phase: 'matchEnd',
      winner: matchWinner,
    };
  }

  // Start next battle
  const nextBattleNumber = state.match.battlesPlayed + 1;

  // Alternate who attacks first each battle
  const newFirstAttacker: PlayerId = state.match.currentBattle.firstAttackerId === 1 ? 2 : 1;

  return {
    ...state,
    phase: 'battleStart',
    match: {
      ...state.match,
      currentBattle: {
        ...createInitialBattleState(nextBattleNumber),
        firstAttackerId: newFirstAttacker,
      },
    },
    currentAttackerId: newFirstAttacker,
    currentDefenderId: newFirstAttacker === 1 ? 2 : 1,
    player1Commitment: createEmptyCommitment(),
    player2Commitment: createEmptyCommitment(),
    revealed: false,
    currentRoundResult: null,
  };
}

function handleAIMakeCommitment(state: GameState, commitment: LaneCommitment): GameState {
  const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
  const playerId: PlayerId = isAttackerTurn ? state.currentAttackerId : state.currentDefenderId;
  const player = state.players[playerId];

  const commitmentKey = playerId === 1 ? 'player1Commitment' : 'player2Commitment';
  const existing = state[commitmentKey];

  // Merge AI commitment with any pre-existing cards (e.g. Motra persistent card)
  const merged: LaneCommitment = {
    left: existing.left || commitment.left,
    center: existing.center || commitment.center,
    right: existing.right || commitment.right,
  };

  // Only remove newly committed cards from hand (not pre-existing persistent cards)
  const lanes: Lane[] = ['left', 'center', 'right'];
  const newlyCommitted = lanes
    .map(lane => (!existing[lane] && merged[lane]) ? merged[lane] : null)
    .filter((c): c is CardInstance => c !== null);

  let newHand = [...player.hand];
  newlyCommitted.forEach(card => {
    newHand = removeCardFromArray(newHand, card.instanceId);
  });

  return {
    ...state,
    players: {
      ...state.players,
      [playerId]: { ...player, hand: newHand },
    },
    [commitmentKey]: merged,
  };
}

// ─────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────

export function canPlayerCommit(state: GameState, playerId: PlayerId): boolean {
  const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
  const expectedPlayer = isAttackerTurn ? state.currentAttackerId : state.currentDefenderId;
  return playerId === expectedPlayer && state.phase === 'commitment';
}

export function mustCommitMinimum(state: GameState, playerId: PlayerId): number {
  const player = state.players[playerId];
  // Must commit at least 1 if you have cards
  return player.hand.length > 0 ? 1 : 0;
}

export function canConfirmCommitment(state: GameState, playerId: PlayerId): boolean {
  const commitment = playerId === 1 ? state.player1Commitment : state.player2Commitment;
  const committedCount = countCommittedCards(commitment);
  const minRequired = mustCommitMinimum(state, playerId);
  return committedCount >= minRequired;
}
